import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScribPostLogo } from "@/components/scribpost-logo";
import { useAppStore } from "@/store";
import { apiFetch } from "@/lib/api-fetch";

export default function VerifyEmail() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const { setEmailVerified, authToken } = useAppStore();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");

  const token = new URLSearchParams(search).get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("No verification token found in the link.");
      return;
    }

    const verify = async () => {
      try {
        const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
        const res = await apiFetch(`${baseUrl}/api/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        const data = await res.json();

        if (!res.ok) {
          setStatus("error");
          setErrorMsg(data.error || "Verification failed");
          return;
        }

        setEmailVerified({
          token: data.token,
          userId: data.user.id,
          email: data.user.email,
          canonicalSessionId: data.user.canonicalSessionId,
          displayName: data.user.displayName,
        });
        setStatus("success");

        setTimeout(() => setLocation("/"), 2500);
      } catch {
        setStatus("error");
        setErrorMsg("Something went wrong. Please try again.");
      }
    };

    verify();
  }, [token]);

  const handleResend = async () => {
    if (!authToken) {
      setLocation("/login");
      return;
    }
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await apiFetch(`${baseUrl}/api/auth/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.error || "Failed to resend. Please try again later.");
        return;
      }
      setErrorMsg("A new verification email has been sent. Check your inbox.");
    } catch {
      setErrorMsg("Failed to resend. Please try again later.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground relative overflow-hidden px-4">
      <div className="bg-orb-1" aria-hidden="true" />
      <div className="bg-orb-2" aria-hidden="true" />
      <div className="bg-grid" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm relative z-10"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl shadow-primary/10 p-8 text-center space-y-5">
          <div className="flex justify-center">
            <ScribPostLogo />
          </div>

          {status === "loading" && (
            <div className="space-y-3 py-4">
              <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
              <p className="text-muted-foreground text-sm">Verifying your email...</p>
            </div>
          )}

          {status === "success" && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="space-y-3 py-4"
            >
              <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto" />
              <h2 className="font-display font-bold text-xl">Email verified!</h2>
              <p className="text-sm text-muted-foreground">
                Redirecting you to the dashboard...
              </p>
            </motion.div>
          )}

          {status === "error" && (
            <div className="space-y-4 py-4">
              <XCircle className="w-14 h-14 text-destructive mx-auto" />
              <h2 className="font-display font-bold text-xl">Verification failed</h2>
              <p className="text-sm text-muted-foreground">{errorMsg}</p>
              <div className="flex flex-col gap-2">
                <Button onClick={handleResend} variant="outline" className="rounded-xl">
                  Resend verification email
                </Button>
                <Button onClick={() => setLocation("/")} className="rounded-xl">
                  Go to dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
